import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import ClearPathLogo from '../components/ClearPathLogo';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <ClearPathLogo size="md" />
          <div className="mt-8 p-6 bg-white border border-[#E6E2DE] rounded-[14px] shadow-sm">
            <AlertCircle size={32} className="text-[#B54A4A] mx-auto mb-3" strokeWidth={1.5} />
            <h2 className="font-serif text-xl text-[#2D2A2E] mb-2">Invalid reset link</h2>
            <p className="font-sans text-sm text-[#6B6670] mb-5">
              This password reset link is missing or invalid. Please request a new one.
            </p>
            <button
              onClick={() => navigate('/auth')}
              className="w-full bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] font-sans font-medium px-6 py-2.5 transition-colors"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setSuccess(true);
      setTimeout(() => navigate('/auth'), 3000);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="min-h-screen bg-cream flex"
    >
      {/* Left: branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#4A2D5E] items-center justify-center p-12">
        <div className="max-w-md">
          <ClearPathLogo variant="white" size="lg" />
          <h2 className="mt-10 font-serif text-3xl text-white leading-tight">
            Your data finally talks back.
          </h2>
          <p className="mt-4 font-sans text-white/70 leading-relaxed">
            ClearPath turns your POS data into plain-English insights — so you can make smarter decisions, every single day.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <ClearPathLogo size="md" />
          </div>

          {success ? (
            <div className="text-center">
              <CheckCircle size={48} className="text-[#2E6B4F] mx-auto mb-4" strokeWidth={1.5} />
              <h1 className="font-serif text-2xl text-[#2D2A2E] mb-2">Password updated!</h1>
              <p className="font-sans text-sm text-[#6B6670] mb-6">
                Your password has been changed successfully. Redirecting you to sign in…
              </p>
              <button
                onClick={() => navigate('/auth')}
                className="w-full bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] font-sans font-medium px-6 py-2.5 transition-colors"
              >
                Sign In Now
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-2xl text-text-primary">Choose a new password</h1>
              <p className="mt-1 font-sans text-sm text-text-secondary">
                Must be at least 8 characters
              </p>

              {error && (
                <div className="mt-4 flex items-center gap-2 p-3 bg-[#B54A4A]/10 border border-[#B54A4A]/20 rounded-[8px]">
                  <AlertCircle size={16} strokeWidth={1.5} className="text-[#B54A4A] flex-shrink-0" />
                  <p className="text-sm font-sans text-[#B54A4A]">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-sans font-medium text-text-primary mb-1.5">New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="Min 8 characters"
                      className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 pr-10 outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                    >
                      {showPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-sans font-medium text-text-primary mb-1.5">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="Repeat your password"
                      className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 pr-10 outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                    >
                      {showConfirm ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Set New Password'}
                </button>
              </form>

              <p className="mt-6 text-center text-sm font-sans text-text-secondary">
                <button onClick={() => navigate('/auth')} className="text-[#4A2D5E] font-medium hover:underline">
                  Back to sign in
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}
